import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import i18n from 'LANG';

dayjs.extend( utc );

/**
 * 获取当前时间，根据给定的阶段调整时间格式。
 * @param {string} stage - 表示时间阶段的字符串，可以是 'hours'| 'day' 或其他值。
 * @returns {dayjs.Dayjs} - 返回一个dayjs对象，表示调整后的时间。
 * if stage = hours, return a Dayjs object base on current hour + 1
 * if stage = day, return a Dayjs object base on current day 00:00:00
 */
export const getCurrentTime = ( stage: string ): dayjs.Dayjs => {

    if ( stage === 'hours' ) {

        const current = dayjs().add( 1, 'h' ).format( 'YYYY MM DD HH' );
        return dayjs( `${current}:00:00`, 'YYYY MM DD HH:mm:SS' );

    } else {

        const current = dayjs().format( 'YYYY MM DD' );
        return dayjs( `${current} 00:00:00`, 'YYYY MM DD HH:mm:SS' );

    }

};

export interface ITimeObject {
    year: string;
    quarter: string;
    month: string;
    week: string;
    dayOfYear: string;
    dayOfWeek: string;
    dateOfMonth: string;
    hour: string;
    hourOf12Decimal: string;
    noon: string;
    minute:  string;
    second:  string;
    monthAbbr:  string;
    monthLong:  string;
    dateOfMonthOrder:  string;
}

/**
 * 将普通时间戳转换为日期对象
 * @param time 普通时间戳（以毫秒为单位）
 * @returns
 */
export const timeResolve = ( time: number ): ITimeObject => {

    const times = dayjs( time ).format( 'YYYY Q MM ww DDD d DD HH hh A mm ss MMM MMMM Do' );
    const timeObj = times.split( ' ' );
    return {

        year: timeObj[ 0 ],
        quarter: timeObj[ 1 ],
        month: timeObj[ 2 ],
        week: timeObj[ 3 ],
        dayOfYear: timeObj[ 4 ],
        dayOfWeek: timeObj[ 5 ],
        dateOfMonth: timeObj[ 6 ],
        hour: timeObj[ 7 ],
        hourOf12Decimal: timeObj[ 8 ],
        noon: timeObj[ 9 ],
        minute: timeObj[ 10 ],
        second: timeObj[ 11 ],
        monthAbbr: timeObj[ 12 ],
        monthLong: timeObj[ 13 ],
        dateOfMonthOrder: timeObj[ 14 ]

    };

};

/**
 * 将Unix时间戳转换为日期对象
 * @param time Unix时间戳（以秒为单位）
 * @returns
 */
export const timeStampResolve = ( time: number ): ITimeObject => {

    const times = dayjs.unix( time / 1000 ).format( 'YYYY Q MM ww DDD d DD HH hh A mm ss MMM MMMM Do' );
    const timeObj = times.split( ' ' );
    return {

        year: timeObj[ 0 ],
        quarter: timeObj[ 1 ],
        month: timeObj[ 2 ],
        week: timeObj[ 3 ],
        dayOfYear: timeObj[ 4 ],
        dayOfWeek: timeObj[ 5 ],
        dateOfMonth: timeObj[ 6 ],
        hour: timeObj[ 7 ],
        hourOf12Decimal: timeObj[ 8 ],
        noon: timeObj[ 9 ],
        minute: timeObj[ 10 ],
        second: timeObj[ 11 ],
        monthAbbr: timeObj[ 12 ],
        monthLong: timeObj[ 13 ],
        dateOfMonthOrder: timeObj[ 14 ]

    };

};

/**
 * @param time 一个表示Pacific Daylight Time的数字时间戳（可能为毫秒）
 * @returns 包含年、季度、月、周等时间信息的对象
 * @description
 * 解析并返回给定时间参数（PDT）的ITimeObject
 * 此函数首先将时间参数转换为UTC，然后减去7小时以适应PDT时区，
 * 并按照指定的日期时间格式进行格式化。最后，将格式化后的字符串分割为
 * 单独的时间元素，存储在一个ITimeObject中返回。
 *
 * 注意：如果time参数已经包含了PDT时区信息，则此函数可能仅进行格式化
 * 和解析操作，而不进行时区转换。
 */
export const timeResolvePDT = ( time: number ): ITimeObject => {

    const times = dayjs.utc( time ).subtract( 7, 'h' ).format( 'YYYY Q MM ww DDD d DD HH hh A mm ss MMM MMMM Do ZZ' );
    const timeObj = times.split( ' ' );
    return {

        year: timeObj[ 0 ],
        quarter: timeObj[ 1 ],
        month: timeObj[ 2 ],
        week: timeObj[ 3 ],
        dayOfYear: timeObj[ 4 ],
        dayOfWeek: timeObj[ 5 ],
        dateOfMonth: timeObj[ 6 ],
        hour: timeObj[ 7 ],
        hourOf12Decimal: timeObj[ 8 ],
        noon: timeObj[ 9 ],
        minute: timeObj[ 10 ],
        second: timeObj[ 11 ],
        monthAbbr: timeObj[ 12 ],
        monthLong: timeObj[ 13 ],
        dateOfMonthOrder: timeObj[ 14 ]

    };

};

/**
 * 根据输入的日期范围字符串，解析并转换为Unix时间戳（毫秒）
 * @param params - 输入参数
 *   - date: 形如 "YYYY-MM-DD HH:HH" 的日期范围字符串，其中部分字段可为 "All"
 *   - rangeStart: 全局范围的起始时间戳（毫秒）
 *   - rangeEnd: 全局范围的结束时间戳（毫秒）
 * @returns {} - 返回解析后的开始和结束时间戳对象
 */
export const resolveToTimestamp = ({

    date,
    rangeStart,
    rangeEnd

}: { date: string; rangeStart: number; rangeEnd: number; }): { start: number; end: number; } => {

    const arr = date.split( '-' );
    const year = arr[ 0 ];
    const month = arr[ 1 ];
    const day = arr[ 2 ];
    const startHour = arr[ 3 ];
    const endHour = arr[ 4 ];
    const format = 'YYYY-MM-DD HH:mm:ss';
    let start = 0;
    let end = 0;
    if ( year === 'All' ) {

        start = rangeStart;
        end = rangeEnd;

    } else if ( month === 'All' ) {

        start = dayjs( `${year}-01-01 00:00:00`, format ).unix() * 1000;
        end = ( dayjs( `${year}-12-01 00:00:00`, format ).add( 1, 'y' ).unix() - 1 ) * 1000;

    } else if ( day === 'All' ) {

        start = dayjs( `${year}-${month}-01 00:00:00`, format ).unix() * 1000;
        end = ( dayjs( `${year}-${month}-01 00:00:00`, format ).add( 1, 'M' ).unix() - 1 ) * 1000;

    } else {

        start = dayjs( `${year}-${month}-${day} ${startHour}:00:00`, 'YYYY-MM-DD H:mm:ss' ).unix() * 1000;
        end = dayjs( `${year}-${month}-${day} ${endHour}:00:00`, 'YYYY-MM-DD H:mm:ss' ).unix() * 1000;

    }
    return {

        start,
        end

    };

};

/**
 * 将传入的时间戳转换为UTC时间戳。
 * @param timeStamp - 输入的时间戳，以秒为单位。
 * @returns 转换为UTC时间戳后的结果，以毫秒为单位。
 */
export const resolveToUtc = ( timeStamp: number ): number => {

    const str = dayjs( timeStamp ).format( 'YYYY-MM-DD HH:mm:ss' );
    return dayjs.utc( str, 'YYYY-MM-DD HH:mm:ss' ).unix() * 1000;

};

/**
 * 将时间戳转换为本地时间的时间戳。
 * @param timeStamp - 输入的时间戳（秒数）。
 * @returns 转换为本地时间的时间戳（毫秒数）。
 */
export const resolveToLocal = ( timeStamp: number ): number => {

    const str = dayjs( timeStamp ).format( 'YYYY-MM-DD HH:mm:ss' );
    return dayjs( str, 'YYYY-MM-DD HH:mm:ss' ).unix() * 1000;

};

/**
 * 返回通用时间格式
 * @param timeStamp
 * @returns
 */
export const formatTime = ( timeStamp: number ): string => {

    const {

        year,
        month,
        dateOfMonth,
        hourOf12Decimal,
        minute,
        noon

    } = timeResolve( timeStamp );

    const time = `${hourOf12Decimal}:${minute} ${noon}`;

    return `${i18n.t( `date_format.default`, {
        year,
        month,
        day: dateOfMonth
    }) as string} ${time}`;

};
